import React from 'react';
import { useSnackbar } from '../../../hooks';
import { isCrmError, useCrmScheduling, useSalesAccess } from '../../../services/crm';
import { MeetingWrapUp } from './MeetingWrapUp.component';
import { useMeetingWrapUps } from './useMeetingWrapUps';
import { dueAt, WrapUpDraft } from './wrapup.helpers';

// #region MeetingWrapUpQueue
/**
 * Rides along with every sales page. When an analysed call is waiting, the
 * wrap-up card appears over whatever the rep is doing and keeps coming back
 * (after "Later", after a reload, on another device) until it is handled.
 */
export const MeetingWrapUpQueue: React.FunctionComponent<{}> = (): React.ReactElement | null => {
    const [{ isRep, loading }] = useSalesAccess();
    const { queue, remove, refresh } = useMeetingWrapUps(isRep && !loading);
    const [scheduling] = useCrmScheduling();
    const apiRef = React.useRef(scheduling);
    apiRef.current = scheduling;
    const [, setSnackbar] = useSnackbar();
    const [busy, setBusy] = React.useState<boolean>(false);
    // "Later" also hides the card locally until the server's snooze expires, so the next poll does not bring it straight back.
    const [hidden, setHidden] = React.useState<Set<number>>(new Set());

    const visible = queue.filter((w) => !hidden.has(w.bot.id));
    const current = visible[0] ?? null;
    if (!current) return null;

    const fail = (m: string) => setSnackbar({ show: true, snackbarLevel: 'error', text: m });

    const wrapUp = async (draft: WrapUpDraft) => {
        setBusy(true);
        const r = await apiRef.current.completeWrapUp(current.bot.id, {
            outcome: draft.outcome, summary: draft.summary.trim(),
            notes: draft.notes.filter((n) => n.on && n.text.trim()).map((n) => n.text.trim()),
            tasks: draft.tasks.filter((t) => t.on && t.text.trim()).map((t) => ({ text: t.text.trim(), due_at: dueAt(t.due) })),
            person: draft.person ? { name: draft.person.name.trim(), email: draft.person.email.trim(), company: draft.person.company.trim() || null } : null
        });
        setBusy(false);
        if (isCrmError(r)) return fail(r.message);
        remove(current.bot.id);
        const n = r.tasks_created;
        setSnackbar({ show: true, snackbarLevel: 'success', text: `Wrapped up${n ? ` — ${n} task${n === 1 ? '' : 's'} added` : ''}${r.activity_written ? ', notes on their lead' : ''}.` });
        refresh();
    };
    const later = async () => {
        setHidden((h) => new Set([...Array.from(h), current.bot.id]));
        const r = await apiRef.current.snoozeWrapUp(current.bot.id, 60);
        if (isCrmError(r)) fail(r.message);
        else setSnackbar({ show: true, snackbarLevel: 'success', text: 'Back in an hour.' });
    };
    const skip = async () => {
        setBusy(true);
        const r = await apiRef.current.skipWrapUp(current.bot.id);
        setBusy(false);
        if (isCrmError(r)) return fail(r.message);
        remove(current.bot.id);
    };

    return (
        <MeetingWrapUp
            key={current.bot.id} item={current} position={{ index: 0, total: visible.length }} busy={busy}
            onWrapUp={wrapUp} onLater={later} onSkip={skip}
        />
    );
};
